import { TRPCClientError, type TRPCLink } from "@trpc/client";
import type { AnyRouter } from "@trpc/server";
import { observable } from "@trpc/server/observable";

export type ErrorHandlerLinkOptions = {
  onTokenInvalid: () => void | Promise<void>;
  onTokenExpired: () => Promise<void>;
};

/**
 * A reactive tRPC link that observes error responses and handles auth-related error codes:
 * - TOKEN_INVALID / UNAUTHORIZED → calls onTokenInvalid (e.g. logout)
 * - TOKEN_EXPIRED → calls onTokenExpired (e.g. refresh), then retries the operation
 *
 * Concurrent TOKEN_EXPIRED errors share a single refresh promise to avoid duplicate refreshes.
 */
export const errorHandlerLink =
  <AppRouter extends AnyRouter>({
    onTokenInvalid,
    onTokenExpired,
  }: ErrorHandlerLinkOptions): TRPCLink<AppRouter> =>
    () => {
      let refreshPromise: Promise<void> | undefined;

      return ({ next, op }) => {
        return observable((observer) => {
          let subscription = next(op).subscribe({
            next(result) {
              observer.next(result);
            },
            error(err) {
              if (err instanceof TRPCClientError) {
                const errorCode: string | undefined = err.data?.errorCode;

                if (errorCode === "TOKEN_INVALID" || errorCode === "UNAUTHORIZED") {
                  Promise.resolve(onTokenInvalid()).catch(() => { });
                  observer.error(err);
                  return;
                }

                if (errorCode === "TOKEN_EXPIRED") {
                  // Deduplicate concurrent refresh requests
                  refreshPromise ??= onTokenExpired().finally(() => {
                    refreshPromise = undefined;
                  });

                  refreshPromise
                    .then(() => {
                      // Retry the operation after successful refresh
                      subscription = next(op).subscribe(observer);
                    })
                    .catch(() => {
                      observer.error(err);
                    });
                  return;
                }
              }

              observer.error(err);
            },
            complete() {
              observer.complete();
            },
          });

          return () => {
            subscription.unsubscribe();
          };
        });
      };
    };
